import type { TowerTypeConfig } from "@/types/tower.types"

// Class names for tower units (7 levels, increasingly powerful)
export const ARCHER_CLASS_NAMES: string[] = [
  "Bowman",
  "Archer",
  "Sharpshooter",
  "Marksman",
  "Longbowman",
  "Deadeye",
  "Grandmaster",
]

export const MAGE_CLASS_NAMES: string[] = [
  "Acolyte",
  "Adept",
  "Mage",
  "Conjurer",
  "Sorcerer",
  "Wizard",
  "Archmage",
]

export const SIEGE_CLASS_NAMES: string[] = [
  "Tinkerer",
  "Sapper",
  "Engineer",
  "Bombardier",
  "Demolitionist",
  "Siegemaster",
  "Warmaster",
]

// Get class name for a tower type and level
export const getTowerClassName = (towerType: string, level: number): string => {
  const index = Math.max(0, Math.min(level - 1, 6))
  switch (towerType) {
    case "archer":
      return ARCHER_CLASS_NAMES[index]
    case "mage":
      return MAGE_CLASS_NAMES[index]
    case "siege":
      return SIEGE_CLASS_NAMES[index]
    default:
      return ""
  }
}

export const towerTypes: Record<string, TowerTypeConfig> = {
  archer: {
    id: "archer",
    name: "Archer Tower",
    description: "Fast-firing tower with piercing arrows",
    baseColor: "#8B4513",
    tileWidth: 1,
    tileHeight: 1,
    attackType: "pierce",
    upgrades: [
      { level: 1, cost: 50, damage: 8, range: 180, attackSpeed: 50, projectileSpeed: 8, maxHealth: 150 },
      { level: 2, cost: 60, damage: 12, range: 195, attackSpeed: 46, projectileSpeed: 9, maxHealth: 200 },
      { level: 3, cost: 75, damage: 16, range: 210, attackSpeed: 42, projectileSpeed: 10, maxHealth: 260 },
      { level: 4, cost: 90, damage: 21, range: 225, attackSpeed: 38, projectileSpeed: 11, maxHealth: 330 },
      { level: 5, cost: 110, damage: 27, range: 240, attackSpeed: 34, projectileSpeed: 12, maxHealth: 410 },
      { level: 6, cost: 135, damage: 34, range: 255, attackSpeed: 30, projectileSpeed: 13, maxHealth: 500 },
      { level: 7, cost: 165, damage: 42, range: 270, attackSpeed: 26, projectileSpeed: 14, maxHealth: 600 },
    ],
  },
  mage: {
    id: "mage",
    name: "Mage Tower",
    description: "Powerful magical attacks, strong vs heavy armor",
    baseColor: "#9370DB",
    tileWidth: 1,
    tileHeight: 1,
    attackType: "magic",
    upgrades: [
      { level: 1, cost: 70, damage: 35, range: 250, attackSpeed: 90, projectileSpeed: 4, maxHealth: 200 },
      { level: 2, cost: 85, damage: 50, range: 270, attackSpeed: 85, projectileSpeed: 4.5, maxHealth: 270 },
      { level: 3, cost: 100, damage: 70, range: 290, attackSpeed: 80, projectileSpeed: 5, maxHealth: 350 },
      { level: 4, cost: 120, damage: 95, range: 310, attackSpeed: 75, projectileSpeed: 5.5, maxHealth: 440 },
      { level: 5, cost: 145, damage: 125, range: 330, attackSpeed: 70, projectileSpeed: 6, maxHealth: 540 },
      { level: 6, cost: 175, damage: 160, range: 350, attackSpeed: 65, projectileSpeed: 6.5, maxHealth: 660 },
      { level: 7, cost: 210, damage: 200, range: 370, attackSpeed: 60, projectileSpeed: 7, maxHealth: 800 },
    ],
  },
  siege: {
    id: "siege",
    name: "Siege Tower",
    description: "Devastating area attacks, strong vs fortified",
    baseColor: "#8B4513",
    tileWidth: 1,
    tileHeight: 1,
    attackType: "siege",
    upgrades: [
      { level: 1, cost: 100, damage: 80, range: 500, attackSpeed: 150, projectileSpeed: 3, maxHealth: 300 },
      { level: 2, cost: 125, damage: 120, range: 540, attackSpeed: 140, projectileSpeed: 3.5, maxHealth: 400 },
      { level: 3, cost: 155, damage: 170, range: 580, attackSpeed: 130, projectileSpeed: 4, maxHealth: 520 },
      { level: 4, cost: 190, damage: 230, range: 620, attackSpeed: 120, projectileSpeed: 4.5, maxHealth: 660 },
      { level: 5, cost: 230, damage: 300, range: 660, attackSpeed: 110, projectileSpeed: 5, maxHealth: 820 },
      { level: 6, cost: 280, damage: 380, range: 700, attackSpeed: 100, projectileSpeed: 5.5, maxHealth: 1000 },
      { level: 7, cost: 340, damage: 480, range: 740, attackSpeed: 90, projectileSpeed: 6, maxHealth: 1200 },
    ],
  },
  barracks: {
    id: "barracks",
    name: "Barracks",
    description: "Spawns soldiers to block and fight enemies",
    baseColor: "#3b82f6",
    tileWidth: 1,
    tileHeight: 1,
    attackType: "normal",
    upgrades: [
      { level: 1, cost: 80, damage: 12, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 250 },
      { level: 2, cost: 95, damage: 18, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 330 },
      { level: 3, cost: 115, damage: 25, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 420 },
      { level: 4, cost: 140, damage: 34, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 520 },
      { level: 5, cost: 170, damage: 45, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 640 },
      { level: 6, cost: 205, damage: 58, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 780 },
      { level: 7, cost: 250, damage: 75, range: 0, attackSpeed: 0, projectileSpeed: 0, maxHealth: 950 },
    ],
  },
  shadowblade: {
    id: "shadowblade",
    name: "Shadowblade",
    description: "Elite rapid-fire assassin tower",
    baseColor: "#1a1a2e",
    isPremium: true,
    premiumCostCrystals: 10,
    tileWidth: 1,
    tileHeight: 1,
    attackType: "pierce",
    upgrades: [
      { level: 1, cost: 50, damage: 20, range: 210, attackSpeed: 24, projectileSpeed: 12, maxHealth: 180 },
      { level: 2, cost: 75, damage: 36, range: 230, attackSpeed: 20, projectileSpeed: 14, maxHealth: 280 },
      { level: 3, cost: 100, damage: 60, range: 260, attackSpeed: 16, projectileSpeed: 16, maxHealth: 400 },
    ],
  },
  shrine: {
    id: "shrine",
    name: "Shrine",
    description: "Provides heroism buffs to heroes",
    baseColor: "#6b8cff",
    tileWidth: 1,
    tileHeight: 1,
    attackType: "normal",
    upgrades: [
      { level: 1, cost: 60, damage: 0, range: 150, attackSpeed: 0, projectileSpeed: 0, maxHealth: 200 },
      { level: 2, cost: 75, damage: 0, range: 175, attackSpeed: 0, projectileSpeed: 0, maxHealth: 280 },
      { level: 3, cost: 95, damage: 0, range: 200, attackSpeed: 0, projectileSpeed: 0, maxHealth: 370 },
      { level: 4, cost: 120, damage: 0, range: 225, attackSpeed: 0, projectileSpeed: 0, maxHealth: 470 },
      { level: 5, cost: 150, damage: 0, range: 250, attackSpeed: 0, projectileSpeed: 0, maxHealth: 580 },
      { level: 6, cost: 185, damage: 0, range: 275, attackSpeed: 0, projectileSpeed: 0, maxHealth: 700 },
      { level: 7, cost: 225, damage: 0, range: 300, attackSpeed: 0, projectileSpeed: 0, maxHealth: 850 },
    ],
  },
}
